import React, { useMemo, Fragment, useEffect, useState } from "react";
import {
    MaterialReactTable,
    MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
    MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
    MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from 'material-react-table';
import Typography from "@mui/material/Typography";
import ExportButton from '../../../common/otherCommonComponents/ExportButton';
import {
  useGetSiteCoreCMSData,
  useTrackActivityMutation,
} from "../../../reactQueries/commonRQHooks";
import { useGetLoggedInUserDetails } from "../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from "@mui/icons-material/Edit";
import { Link, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import { dateformatConverter } from "../../../common/utilities/dates/dateformatConverter";

import { useViewClientUsersListMutation } from "../../../reactQueries/AppApi/rqHooksForClientUsers";
import Stack from "@mui/material/Stack";
import { clientUserCreatePath, clientUserEditPath } from "../../../routes/routePaths";
import MpgErrorDialog from "../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../common/tooltips/HtmlTooltip";
import { getAccessTypes } from "../../../hooksAndFunctions/functions/getAccessTypes";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const getClientUserFullName = (clientUser) => {
  return `${clientUser?.FirstName || ''} ${clientUser?.LastName || ''}`.trim();
};

const getRegistrationStatus = (clientUser) => {
  if (clientUser?.IsRegistered) {
    return 'Yes';
  }

  return clientUser?.LastLogin ? 'Incomplete' : 'No';
};


const ViewClientUser = () => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;
const { mutate: trackActivityMutate } = useTrackActivityMutation();
    const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
    const {
        hasA1Access,
    } = clientPortalAdminType

    const canExportXls = isEnterpriseAdminType || hasA1Access;
  const { mutate: getClientAccountList, isSuccess, data: clientUsersData, isError, isLoading: isLoadingOnClientUserData } = useViewClientUsersListMutation();

  const { data: adminClientUserCMSDataFromAPI } = useGetSiteCoreCMSData({pagename: "ClientUsers"});

  const [clientUsersCMSData, setClientUsersCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  useEffect(() => {
    getClientAccountList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const fetchClientUsersCMSData = () =>{
    adminClientUserCMSDataFromAPI?.Pages?.forEach((el) => {
       if (el.Key === "ClientUsers") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setClientUsersCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
    });
  }

  const fetchClientUserListLabel = (key) => {
    const labels = clientUsersCMSData?.pageContent?.ClientUsersList?.Labels;
    return returnLabel(key, labels);
  }

  const fetchCommonCMSLabel = (key) => {
    const labels = clientUsersCMSData?.pageContent?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  }

  
  const clientUsersTitle = clientUsersCMSData?.title;
  const exportFileName = `${clientUsersTitle }.xlsx`;
  const exportWorksheetName = clientUsersTitle;
  
    const exportColumns = [
    { id: 'userFullName', header: fetchClientUserListLabel('ClientUsersList.Name'), getValue: getClientUserFullName },
    { id: 'EmailAddress', header: fetchClientUserListLabel('ClientUsersList.Email'), getValue: (row) => row?.EmailAddress || '' },
    { id: 'clientAccountName', header: fetchClientUserListLabel('ClientUsersList.ClientAccount'), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'clientSubAccountName', header: fetchClientUserListLabel('ClientUsersList.ClientSubAccount'), getValue: (row) => row?.ClientSubAccountName || '' },
    { id: 'PermissionDescription', header: fetchClientUserListLabel('ClientUsersList.PermissionLevel'), getValue: (row) => row?.PermissionDescription || '' },
    { id: 'StatusTypeDescription', header: fetchClientUserListLabel('ClientUsersList.Status'), getValue: (row) => row?.StatusTypeDescription || '' },
    { id: 'isRegistered', header: fetchClientUserListLabel('ClientUsersList.Registration'), getValue: getRegistrationStatus },
    { id: 'lastLogin', header: fetchClientUserListLabel('ClientUsersList.LastLogin'), getValue: (row) => row?.LastLogin ? dateformatConverter(row.LastLogin, "DD MMM YYYY hh:mm A") : '' },
    { id: 'createdOn', header: fetchClientUserListLabel('ClientUsersList.CreatedOn'), getValue: (row) => row?.CreatedTimestamp ? dateformatConverter(row.CreatedTimestamp, "DD MMM YYYY hh:mm A") : '' },
  ];
  
  useEffect(() => {
    fetchClientUsersCMSData();
   }, [adminClientUserCMSDataFromAPI]);

  const columns = useMemo(
    () => [
      {
        accessorFn: getClientUserFullName,
        id: 'userFullName',
        header: fetchClientUserListLabel('ClientUsersList.Name'), //"Name",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderName") }
      },
      {
        accessorKey: "EmailAddress",
        header: fetchClientUserListLabel('ClientUsersList.Email'), //"Email",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderEmail") }
      },
      {
        accessorFn: (row) => {
          const val = row.ClientAccountName
          return val || ""
        },
        id: 'clientAccountName',
        header: fetchClientUserListLabel('ClientUsersList.ClientAccount'), //"Client Account",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderClientAccount") }
      },
      {
        accessorFn: (row) => {
          const val = row.ClientSubAccountName
          return val || ""
        },
        id: 'clientSubAccountName',
        header: fetchClientUserListLabel('ClientUsersList.ClientSubAccount'), //"Client Sub-Account",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderClientSubAccount") }
      },
      {
        accessorKey: "PermissionDescription",
        header: fetchClientUserListLabel('ClientUsersList.PermissionLevel'), //"Permission Level",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderPermissionLevel") }
      },
      {
        accessorKey: "StatusTypeDescription",
        header: fetchClientUserListLabel('ClientUsersList.Status'), //"Status",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderStatus") }
      },
      {
        accessorFn: getRegistrationStatus,
        id: 'isRegistered',
        header: fetchClientUserListLabel('ClientUsersList.Registration'), //"Registration",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderRegistration") }
      },
      {
        id: 'lastLogin',
        header: fetchClientUserListLabel('ClientUsersList.LastLogin'), //"Last Login",
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.LastLogin, "DD MMM YYYY hh:mm A");
          return `${data}`
        },
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderLastLogin") }
      },
      {
        id: 'createdOn',
        header: fetchClientUserListLabel('ClientUsersList.CreatedOn'), //"Created On",
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.CreatedTimestamp, "DD MMM YYYY hh:mm A");
          return `${data}`
        },
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchClientUserListLabel("ClientUsersList.PlaceholderCreatedOn")}
      },

    ],
    [clientUsersCMSData]
  );

  /**redirect hook */
  let navigate = useNavigate();

  return (
    <Fragment>
      {
        !!isLoadingOnClientUserData && fetchCommonCMSLabel('Common.Waitloading')
      }
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {/* Client Users */}
        {clientUsersCMSData.title}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={clientUserCreatePath}
        >
          {/* Create Client User */}
          {fetchClientUserListLabel('ClientUsersList.CreateClientUser')}
        </Button>
      </Stack>

      {/**incase successful call but empty response */}
      {!!isSuccess && !clientUsersData?.data?.length && (<NoTableDataComp />)}

      {!!isSuccess && !!clientUsersData?.data.length && (
        <MaterialReactTable
          columns={columns}
          data={clientUsersData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnClientUserData,
            columnPinning: {
              left: ['mrt-row-actions', 'userFullName']
            }
          }}
          enableStickyHeader
          enableRowActions

          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchClientUserListLabel('ClientUsersList.Actions'), //Actions
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
          }}
renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientUserListLabel("ClientUsersList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(clientUserEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )

          }}
        />
      )}

      <MpgErrorDialog
        open={!!isError}
      />
    </Fragment>
  );
};

export default ViewClientUser;
